import { useState, useEffect } from 'react';
import useDebounce from '../hooks/useDebounce';
import {
  ArrowLeft,
  Search,
  MessageCircle,
  UserRound,
  Clock,
  Trash2,
  AlertCircle,
  Paperclip,
} from 'lucide-react';

import { api } from '../services/client';
import { useTranslation } from '../i18n';
import { useDeleteMessage } from '../hooks/queries/useSpaceQueries';
import { getImageUrl } from '../utils/imageUrl';
import ImagePreviewOverlay from './ImagePreviewOverlay';
import { IPaginatedMessages, IMessage, IRoomDetail } from '../types';
import { formatDateTime } from '../utils/formatDate';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent } from './ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';

interface IChatHistoryProps {
  roomId: string;
  onBack: () => void;
}

const ChatHistory = ({ roomId, onBack }: IChatHistoryProps) => {
  const { t } = useTranslation();
  const [roomInfo, setRoomInfo] = useState<IRoomDetail | null>(null);
  const [messages, setMessages] = useState<IMessage[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const deleteMessage = useDeleteMessage();
  const limit = 20;

  useEffect(() => {
    loadRoomInfo();
  }, [roomId]);

  useEffect(() => {
    loadMessages();
  }, [roomId, page, filter]);

  // Debounced search on type
  useDebounce(
    () => {
      setPage(1);
      loadMessages();
    },
    300,
    [search],
  );

  const loadRoomInfo = async () => {
    try {
      const data = await api.get<IRoomDetail>(
        `/backoffice/rooms/${roomId}/detail`,
      );
      setRoomInfo(data);
    } catch (e) {
      console.error(e);
    }
  };

  const loadMessages = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
      });

      if (search) params.set('search', search);
      if (filter !== 'all') params.set('filter', filter);

      const data = await api.get<IPaginatedMessages>(
        `/backoffice/rooms/${roomId}/messages?${params}`,
      );
      setMessages(data.messages);
      setTotal(data.total);
      setTotalPages(data.totalPages);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteMessage = async (messageId: string) => {
    deleteMessage.mutate(messageId, {
      onSuccess: () => {
        setConfirmDelete(null);
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === messageId ? { ...msg, isDeleted: true } : msg,
          ),
        );
      },
    });
  };

  const reportedCount = roomInfo?.reportedCount || 0;

  const stats = [
    {
      label: t('chathistory.total_messages'),
      value: (roomInfo?.messageCount || total).toLocaleString(),
      icon: <MessageCircle className="w-8 h-8 text-blue-500" />,
    },
    {
      label: t('chathistory.reported_messages'),
      value: reportedCount.toString(),
      icon: <AlertCircle className="w-8 h-8 text-red-500" />,
    },
    {
      label: t('chathistory.active_members'),
      value: (roomInfo?.memberCount || 0).toString(),
      icon: <UserRound className="w-8 h-8 text-green-500" />,
    },
  ];

  return (
    <div className="p-4 lg:p-8 flex flex-col gap-6">
      {/* Back button */}
      <Button
        variant="ghost"
        onClick={onBack}
        className="flex items-center gap-1 text-sm text-foreground w-fit"
      >
        <ArrowLeft className="w-4 h-4" /> {t('chathistory.back_to_detail')}
      </Button>

      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-lg bg-purple-500 flex items-center justify-center text-white overflow-hidden shrink-0">
          {roomInfo?.avatar ? (
            <img
              src={getImageUrl(roomInfo.avatar)!}
              alt=""
              className="w-full h-full object-cover"
            />
          ) : (
            <MessageCircle className="w-6 h-6" />
          )}
        </div>
        <div>
          <h1 className="text-3xl font-bold text-foreground">
            {roomInfo?.name || '—'}
          </h1>
          <p className="text-base text-muted-foreground mt-1">
            {roomInfo?.memberCount || 0} {t('chathistory.members_label')} ·{' '}
            {roomInfo?.messageCount || total} {t('chathistory.messages_label')}
          </p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        {stats.map(({ label, value, icon }) => (
          <Card key={label} className="py-5 px-5 shadow-sm">
            <CardContent className="p-0">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium text-muted-foreground">
                    {label}
                  </div>
                  <div className="text-[26px] font-bold leading-[1.2] text-foreground">
                    {value}
                  </div>
                </div>
                {icon}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Search and Filters */}
      <Card className="shadow-sm">
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            <div className="relative flex-1">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                <Search className="w-5 h-5" />
              </span>
              <Input
                type="text"
                placeholder={t('chathistory.search_placeholder')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="flex gap-2">
              <Button
                variant={filter === 'all' ? 'default' : 'outline'}
                onClick={() => {
                  setFilter('all');
                  setPage(1);
                }}
                className="px-4 py-2.5 rounded-lg text-sm font-medium"
              >
                {t('chathistory.filter_all')}
              </Button>
              <Button
                variant={filter === 'normal' ? 'default' : 'outline'}
                onClick={() => {
                  setFilter('normal');
                  setPage(1);
                }}
                className="px-4 py-2.5 rounded-lg text-sm font-medium"
              >
                {t('chathistory.filter_normal')}
              </Button>
              <Button
                variant={filter === 'reported' ? 'default' : 'outline'}
                onClick={() => {
                  setFilter('reported');
                  setPage(1);
                }}
                className="px-4 py-2.5 rounded-lg text-sm font-medium"
              >
                {t('chathistory.filter_reported')}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Messages List */}
      <Card className="shadow-sm">
        <CardContent className="p-6">
          {loading ? (
            <div className="py-12 text-center text-sm text-muted-foreground">
              {t('common.loading')}
            </div>
          ) : messages.length === 0 ? (
            <div className="py-12 text-center text-sm text-muted-foreground">
              {t('rooms.no_messages')}
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              {messages.map((msg) => (
                <div
                  key={msg.id}
                  className="rounded-xl border border-border p-4"
                >
                  <div className="flex items-start gap-3">
                    {/* Avatar */}
                    <div
                      className="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-medium shrink-0 overflow-hidden"
                      style={{ backgroundColor: '#3b82f6' }}
                    >
                      {msg.sender?.avatar ? (
                        <img
                          src={getImageUrl(msg.sender.avatar)!}
                          alt=""
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        (
                          msg.sender?.displayName ||
                          msg.sender?.username ||
                          '?'
                        ).charAt(0)
                      )}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-sm font-medium text-foreground">
                          {msg.sender?.displayName ||
                            msg.sender?.username ||
                            t('rooms.system')}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          ID: {msg.senderId?.slice(0, 8) || '—'}
                        </span>
                      </div>

                      {/* A removed message keeps its content here: this is
                          the moderation audit view, so a moderator reviewing
                          a flagged message sees what was actually said. It is
                          badged rather than hidden. Messages deleted before
                          2026-09-23 carry the client placeholder as their
                          body (the API used to overwrite it), so those render
                          the badge with the placeholder text. */}
                      <div className="flex flex-col gap-1.5">
                        {msg.isDeleted && (
                          <span className="w-fit rounded bg-red-50 px-1.5 py-0.5 text-xs font-medium text-red-700 ring-1 ring-red-200">
                            {t('chathistory.message_deleted')}
                          </span>
                        )}
                        {/* Caption and media are not mutually exclusive: an
                            image or attachment can carry text, so render both
                            rather than branching on type alone. */}
                        <div className="flex flex-col gap-1.5">
                          {msg.content && (
                            <p className="text-sm text-[#364153]">
                              {msg.content}
                            </p>
                          )}

                          {msg.type === 'image' &&
                            (msg.imageUrl ? (
                              // getImageUrl resolves the signed path against
                              // the media base; a bare src would resolve
                              // against the current route and 404.
                              <button
                                type="button"
                                onClick={() =>
                                  setPreviewImage(getImageUrl(msg.imageUrl)!)
                                }
                                className="w-fit cursor-zoom-in rounded-lg"
                                aria-label={t('chathistory.view_image')}
                              >
                                <img
                                  src={getImageUrl(msg.imageUrl)}
                                  alt=""
                                  className="max-w-[200px] rounded-lg"
                                />
                              </button>
                            ) : (
                              <span className="text-sm text-muted-foreground">
                                {t('rooms.image')}
                              </span>
                            ))}

                          {msg.type === 'attachment' && msg.attachmentKey && (
                            <a
                              href={getImageUrl(msg.attachmentKey)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex w-fit items-center gap-2 text-sm text-blue-600 hover:underline"
                            >
                              <Paperclip className="w-4 h-4 shrink-0" />
                              <span>
                                {msg.attachmentName ||
                                  t('chathistory.attachment')}
                              </span>
                            </a>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center gap-1 mt-1.5">
                        <Clock className="w-3 h-3 text-muted-foreground" />
                        <span className="text-xs text-muted-foreground">
                          {formatDateTime(msg.createdAt)}
                        </span>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="shrink-0">
                      {!msg.isDeleted && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setConfirmDelete(msg.id)}
                          className="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg"
                          title={t('rooms.delete_message')}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">
            {t('common.page_info', {
              page: page.toString(),
              totalPages: totalPages.toString(),
            })}
          </span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setPage(Math.max(1, page - 1))}
              disabled={page <= 1}
              className="px-4 py-2 rounded-lg text-sm"
            >
              {t('common.prev_page')}
            </Button>
            <Button
              variant="outline"
              onClick={() => setPage(Math.min(totalPages, page + 1))}
              disabled={page >= totalPages}
              className="px-4 py-2 rounded-lg text-sm"
            >
              {t('common.next_page')}
            </Button>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={!!confirmDelete}
        onOpenChange={(open) => {
          if (!open) setConfirmDelete(null);
        }}
      >
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>{t('rooms.delete_message')}</DialogTitle>
            <DialogDescription>
              {t('chathistory.delete_confirm')}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDelete(null)}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="destructive"
              onClick={() =>
                confirmDelete && handleDeleteMessage(confirmDelete)
              }
            >
              {t('common.confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {previewImage && (
        <ImagePreviewOverlay
          src={previewImage}
          onClose={() => setPreviewImage(null)}
        />
      )}
    </div>
  );
};

export default ChatHistory;
